"use client";

import { c as _c } from "react/compiler-runtime";
import { PartyPopper } from "lucide-react";
import type { UploadTilePreview } from "@/lib/upload-tile-preview";
interface UploadSuccessFanProps {
  previews: readonly UploadTilePreview[];
}
const FAN_SIZE = 5;
const CONFETTI_COLORS = ["#F9B144", "#FFCE7F", "#FED7AA", "#E3971D", "#FFFFFF"];

/**
 * Celebrates a batch the server has confirmed. Loads canvas-confetti only
 * here, so the library stays out of the upload page's initial bundle.
 */
export async function celebrateSavedUploads(): Promise<void> {
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  try {
    navigator.vibrate?.(18);
  } catch {
    // Vibration is optional and unsupported on iOS.
  }
  const {
    default: confetti
  } = await import("canvas-confetti");
  const shared = {
    colors: CONFETTI_COLORS,
    disableForReducedMotion: true,
    scalar: 0.9,
    ticks: 220
  };
  confetti({
    ...shared,
    particleCount: 70,
    spread: 75,
    startVelocity: 38,
    origin: {
      x: 0.5,
      y: 0.4
    }
  });
  window.setTimeout(() => {
    confetti({
      ...shared,
      particleCount: 30,
      angle: 60,
      spread: 55,
      origin: {
        x: 0.15,
        y: 0.65
      }
    });
    confetti({
      ...shared,
      particleCount: 30,
      angle: 120,
      spread: 55,
      origin: {
        x: 0.85,
        y: 0.65
      }
    });
  }, 180);
}

// The guest's own saved photos fan out like a hand of prints. Falls back to
// the party icon when none of the files had an embedded thumbnail.
export default function UploadSuccessFan(t0) {
  const $ = _c(12);
  const {
    previews
  } = t0;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== previews) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const shown = previews.slice(0, FAN_SIZE);
      if (shown.length === 0) {
        let t5;
        if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = <PartyPopper className="mb-4 h-16 w-16 text-gg-orange-500 motion-safe:animate-upload-pop" aria-hidden="true" />;
          $[5] = t5;
        } else {
          t5 = $[5];
        }
        t4 = t5;
        break bb0;
      }
      const middle = (shown.length - 1) / 2;
      t1 = "relative mb-5 h-24 w-full";
      t2 = "true";
      let t5;
      if ($[6] !== middle) {
        t5 = (preview, index) => {
          const offset = index - middle;
          return <div key={index} className="absolute left-1/2 top-1 w-[72px] -ml-9 rounded-[4px] bg-white p-1 pb-3 shadow-[0_10px_22px_-12px_rgba(56,40,22,0.6)] ring-1 ring-[#3b2a17]/[0.06] motion-safe:animate-upload-fan-in" style={{
            rotate: `${offset * 8}deg`,
            translate: `${offset * 34}px ${Math.abs(offset) * 6}px`,
            animationDelay: `${120 + index * 70}ms`,
            zIndex: index
          }}><div className="aspect-square overflow-hidden rounded-[2px] bg-[#f3d9b8]"><img src={preview.url} alt="" className="h-full w-full object-cover [image-orientation:none]" style={{
                transform: preview.transform
              }} draggable={false} /></div></div>;
        };
        $[6] = middle;
        $[7] = t5;
      } else {
        t5 = $[7];
      }
      t3 = shown.map(t5);
    }
    $[0] = previews;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
    t4 = $[4];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t5 = <div className={t1} aria-hidden={t2}>{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}